import React, { useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import axios from 'axios';
import clsx from 'clsx';
import Spinner from 'components/spinner/Spinner';
import { useSelector } from 'react-redux';
import { NullableCommunique } from 'types/types';
import { RootState } from 'store/store';
import MetaTag from 'components/meta';
import styles from 'components/medicalHistory/page.module.scss';
import Button from 'components/common/button';
import InputText from 'components/common/InputText';
import { usePersistMHReducer } from 'reducer/medicalHistory';
import { getAddressString, isEmptyString, parseErrorMessage } from 'utils/helper';
import OutlineButton from 'components/common/outlineButton';
import {
  ERxPharmacySearchResponseDTO,
  ERxPharmacySearchResultDTO,
} from 'artifacts/dtos';
import { NullablePatientDTO } from 'types/patients';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Alert, { AlertVariant } from 'components/common/Alerts';
import AlertTitle from '@mui/material/AlertTitle';
import isNil from 'lodash/isNil';
import { useERxPharmacySearch } from 'hooks/useApi';

const enum InputTextName {
  zip = 'zip',
  pharmacyName = 'pharmacyName',
  pharmacyAddress = 'pharmacyAddress',
}

const title = 'Pharmacy Information';
type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function PharmacyInformationScreen({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const [isExpended, setIsExpended] = useState(false);
  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;

  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;

  const [pharmacyName, setPharmacyName] = useState('');
  const [pharmacyAddress, setPharmacyAddress] = useState('');
  const [zip, setZip] = useState('');
  const {
    mutateAsync: searchERxPharmacy,
    isPending: isLoading,
  } = useERxPharmacySearch();

  const [errors, _setErrors] = useState<Record<string, boolean>>({});

  const setErrors = (errors: Record<string, boolean>) => {
    _setErrors((previousErrors) => ({
      ...previousErrors,
      ...errors,
    }));
  };

  const [data, setData] = useState<ERxPharmacySearchResponseDTO>();

  const [selectedPharmacy, setSelectedPharmacy] = useState<ERxPharmacySearchResultDTO>();

  const [errorMessage, setErrorMessage] = useState('');
  const [, dispatch] = usePersistMHReducer();

  const clickNextOrSubmit = async (nextPage: number) => {
    const payload = {
      selectedPharmacy,
    };

    await dispatch({
      type: actionPage,
      name: title,
      value: payload,
    });
    nextOrSubmitButton(nextPage);
  };

  const onChangeInputText = (
    event: React.ChangeEvent<HTMLInputElement>,
    name: InputTextName,
  ) => {
    const { value } = event.target;
    if (name === InputTextName.pharmacyName) {
      setPharmacyName(value.trimStart());
      setErrors({ pharmacyName: false });
    }
    if (name === InputTextName.pharmacyAddress) {
      setPharmacyAddress(value.trimStart());
      setErrors({ pharmacyAddress: false });
    }
    if (name === InputTextName.zip) {
      setZip(value.trimStart());
      setErrors({ zip: false });
    }
    setErrorMessage('');
  };

  const search = async () => {
    const checkFields = [
      { key: 'pharmacyName', value: pharmacyName },
      { key: 'pharmacyAddress', value: pharmacyAddress },
      { key: 'zip', value: zip },
    ];

    if (checkFields.every((o) => isEmptyString(o.value))) {
      setErrorMessage('Please enter either Pharmacy name or Zipcode or Address or both for better search results');
      return;
    }
    const errorList: Record<string, boolean> = {};
    const checkIfEmpty = checkFields.some((field) => {
      const result = !isEmptyString(field.value) && field.value.trim().length < 3;
      errorList[field.key] = result;
      return result;
    });
    setErrors(errorList);

    if (checkIfEmpty) {
      setErrorMessage('Please input at least 3 characters');
      return;
    }

    const params = {
      name: isEmptyString(pharmacyName) ? undefined : pharmacyName,
      zip: isEmptyString(zip) ? undefined : zip,
      address: isEmptyString(pharmacyAddress) ? undefined : pharmacyAddress,
      organizationId: communiqueData?.organizationId ?? undefined,
    };

    try {
      const response = await searchERxPharmacy(params);
      setData(response);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`${parseErrorMessage(error)}`);
      } else {
        setErrorMessage('Something went wrong on PharmacySearch');
      }
      setData({ items: [] });
    }
  };

  const itemCommonClass = clsx('border border-gray-200 rounded py-2 px-3 my-2 leading-5');
  const itemClass = clsx(
    itemCommonClass,
    'hover:border-populate-green cursor-pointer',
  );

  const hasDosespotError = useMemo(() => !isNil(patient?.dosespotError), [patient]);

  return (
    <>
      <MetaTag title={title} />
      <Box pb={1}>
        <Box className={styles.camera}>
          <h2 className={`marginTopZero ${styles.titleText}`}>{title}</h2>
          {errorMessage && (
            <p className="error-message">{errorMessage}</p>
          )}
          <Alert
            severity={AlertVariant.ERROR}
            open={hasDosespotError}
            className="mb-3 !w-full"
          >
            <AlertTitle
              className="!text-sm"
            >
              {patient?.dosespotError?.title ?? ''}
            </AlertTitle>
            <p className="text-xs">
              {patient?.dosespotError?.details ?? ''}
            </p>
          </Alert>
          <Stack className="mt-2">
            <InputText
              onChange={(event) => onChangeInputText(event, InputTextName.pharmacyName)}
              value={pharmacyName}
              error={errors.pharmacyName}
              name="pharmacy-name"
              id="pharmacy-name"
              placeholder="Pharmacy Name"
              type="search"
              autoComplete="off"
              disabled={hasDosespotError}
            />

            <p className={styles.separator} />

            <InputText
              onChange={(event) => onChangeInputText(event, InputTextName.pharmacyAddress)}
              value={pharmacyAddress}
              error={errors.pharmacyAddress}
              id="pharmacy-address"
              name="pharmacy-address"
              placeholder="Pharmacy Address"
              disabled={hasDosespotError}
            />

            <p className={styles.separator} />

            <InputText
              onChange={(event) => onChangeInputText(event, InputTextName.zip)}
              value={zip}
              error={errors.zip}
              id="zip"
              name="zip"
              type="number"
              placeholder="Zip"
              disabled={hasDosespotError}
            />

            <p className={styles.separator} />
            <OutlineButton
              search
              onClick={search}
              disabled={isLoading || hasDosespotError}
            >
              Search
            </OutlineButton>
            <div className="flex">
              <p className="text-sm my-3 font-semibold">Previously Selected Pharmacies</p>
              <Button
                variant="text"
                className="!w-0 hover:bg-transparent"
                classWrap="!w-auto !m-0"
                onClick={() => { setIsExpended(!isExpended); }}
              >
                {isExpended ? <ExpandLessIcon />
                  : <ExpandMoreIcon />}
              </Button>
            </div>
            {isExpended && patient?.pharmacies?.map((item) => (
              <div
                key={item.pharmacyId}
                className={`${itemCommonClass} disabled bg-gray-100`}
              >
                <p className="font-bold text-base">{item.storeName}</p>
                <p className="text-sm text-[#6F767F]">
                  {item.address1}
                </p>
                <p className="text-sm text-[#6F767F]">{item.primaryPhone}</p>
              </div>
            ))}

            {isExpended && (patient?.pharmacies?.length ?? 0) === 0 && (
            <p className="text-sm text-gray-600 text-center my-3">
              No Pharmacy found.
            </p>
            )}

            {data && <p className="text-sm my-3">Search Results</p>}
            {(data?.items ?? []).length === 0 && (
              <p className="text-sm text-gray-600 text-center my-3">
                No Result found
              </p>
            )}
            {isLoading && (
              <div className="my-4">
                <Spinner />
              </div>
            )}

            {!isLoading
              && (data?.items ?? []).map((item, index) => (
                <div
                  role="button"
                  tabIndex={index}
                  key={item.pharmacyId}
                  className={clsx(itemClass, { '!border-populate-green': item.pharmacyId === selectedPharmacy?.pharmacyId })}
                  onClick={() => setSelectedPharmacy(item)}
                  onKeyUp={() => setSelectedPharmacy(item)}
                >
                  <p className="font-bold text-base">{item.storeName}</p>
                  <p className="text-sm text-[#6F767F]">
                    {getAddressString(item.address)}
                  </p>
                  <p className="text-sm text-[#6F767F]">{item.primaryPhone}</p>
                </div>
              ))}
            <div />
          </Stack>
        </Box>
        <div className="next-button-fixed">
          <Button
            onClick={() => clickNextOrSubmit(nextPage)}
          >
            Next
          </Button>
        </div>
      </Box>
    </>
  );
}

export default PharmacyInformationScreen;
